import { ButcherHeader } from '@components/ButcherHeader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faEllipsisVertical,
  faSearch,
  faSort,
  faSortUp,
  faSortDown,
} from '@fortawesome/free-solid-svg-icons';
import { SideNav } from '@components/OrderCreation/OrderSideNav';
import Pagination from '@components/Pagination';
import { useEffect, useState, useRef } from 'react';
import {
  errorMessage,
  OrderProcessingMasterData,
  orderProcessingStatusFilters,
  pagination,
  searchConfig,
  ServiceProcessingCrumbSteps,
} from '@helpers/Constants';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { Axios } from '@helpers/ApiService';
import { useToast } from '@contexts/Toast';
import { OrderStatusBadge } from '@components/OrderStatusBadge';
import { OrderListBreadcrumbs } from '@components/OrderProcessing/OrderListBreadcrumbs';
import { ProcessingListProps } from '@helpers/Models';
import { CreateOrderModal } from '@components/Modal/OrderProcessing/CreateOrderModal';
import { useNavigate } from 'react-router-dom';
import { formatDate, handleDetailsView } from '@services/OrderCreationService';
import { useDismissDropdown } from '@services/DropdownService';

export const ServiceProcessingList: React.FC = () => {
  const { showToastMessage } = useToast();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(pagination.itemsPerPage);
  const [totalPages, setTotalPages] = useState(1);
  const [searchKey, setSearchKey] = useState('');
  const [serviceProcessingList, setServiceProcessingList] = useState<
    ProcessingListProps[]
  >([]);
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const dropdownRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const filterDropdownRef = useRef<HTMLDivElement | null>(null);
  const [debouncedSearchKey, setDebouncedSearchKey] = useState('');
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [selectedOrderStatus, setSelectedOrderStatus] = useState<number | null>(
    null,
  );
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const [isCreatingNewOrder, setisCreatingNewOrder] = useState(false);
  const navigate = useNavigate();

  // ---------- Sorting state ----------
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const selectedOrderStatusLabel =
    orderProcessingStatusFilters.serviceOrderStatus.find(
      (status) => status.value === selectedOrderStatus,
    )?.label ?? 'All Services';

  /**
   * Debounces the `searchKey` input.
   */
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchKey(searchKey);
    }, searchConfig.SEARCH_DELAY);

    return () => {
      clearTimeout(handler);
    };
  }, [searchKey]);

  /**
   * Fetches the list of services whenever pagination, search, filter, or sorting changes.
   */
  useEffect(() => {
    fetchAllServices(
      currentPage,
      debouncedSearchKey,
      itemsPerPage,
      selectedOrderStatus,
      sortField,
      sortDirection,
    );
  }, [currentPage, itemsPerPage, debouncedSearchKey, selectedOrderStatus, sortField, sortDirection]);

  /**
   * Custom hook to dismiss dropdowns on outside click.
   */
  useDismissDropdown({
    refs: [
      ...Array.from(dropdownRefs.current.values()).map((el) => ({
        current: el,
      })),
      { current: filterDropdownRef.current },
    ],
    onClose: () => {
      setActiveDropdown(null);
      setIsFilterDropdownOpen(false);
    },
  });

  /**
   * Fetches a paginated list of service orders from the API.
   */
  const fetchAllServices = async (
    page: number,
    searchKey: string,
    count: number = itemsPerPage,
    status: number | null = selectedOrderStatus,
    field: string = sortField,
    direction: 'asc' | 'desc' = sortDirection,
  ): Promise<void> => {
    try {
      const payload: any = {
        page,
        count,
        searchKey,
      };
      if (status !== null) payload.status = status;
      if (field) {
        payload.sort = { [field]: direction };
      }

      const response = await Axios.post(ApiEndpoints.SERVICE_ORDER_LIST, payload);

      setServiceProcessingList(response?.data?.data.orders);
      setCurrentPage(response?.data?.data.currentPage || 1);
      setTotalPages(response?.data?.data.totalPages || 1);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Toggles the active dropdown state for a given order ID.
   */
  const toggleDropdown = (orderId: number) => {
    setActiveDropdown(activeDropdown === orderId ? null : orderId);
  };

  const handleStatusFilterSelect = (status: number | null): void => {
    setSelectedOrderStatus(status);
    setCurrentPage(1);
    setIsFilterDropdownOpen(false);
  };

  /**
   * Handles column header click to set sort field and direction.
   */
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1); // reset to first page when sorting changes
  };

  /**
   * Renders the sort icon for a given column.
   */
  const renderSortIcon = (field: string) => {
    if (sortField !== field) {
      return <FontAwesomeIcon icon={faSort} className="ml-1 text-gray-300" />;
    }
    if (sortDirection === 'asc') {
      return <FontAwesomeIcon icon={faSortUp} className="ml-1 text-gray-600" />;
    }
    return <FontAwesomeIcon icon={faSortDown} className="ml-1 text-gray-600" />;
  };

  const BreadCrumbsProps = {
    steps: ServiceProcessingCrumbSteps,
    activeItem: 'Service Processing',
    type: OrderProcessingMasterData.SERVICE_PROCESSING,
  };

  return (
    <div className="flex h-screen w-screen gap-1">
      {/* Sidebar */}
      <SideNav active={4} />
      {/* Main Content */}
      <div className="flex-1 p-1 overflow-auto relative">
        {/* Header */}
        <ButcherHeader />
        <OrderListBreadcrumbs orderInfo={BreadCrumbsProps} />
        <div className="flex justify-between items-center p-3">
          <h1 className="font-semibold text-xl px-5 mt-3">
            Service Processing
          </h1>
          <div className="flex items-center gap-2">
            <div className="relative" ref={filterDropdownRef}>
              <button
                type="button"
                onClick={() =>
                  setIsFilterDropdownOpen((previousState) => !previousState)
                }
                className="min-w-[180px] h-10 border border-gray-300 bg-white px-4 rounded text-left text-sm font-medium text-[#1C1B1F] flex items-center justify-between gap-3 hover:border-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                <span>{selectedOrderStatusLabel}</span>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={`text-gray-500 transition-transform duration-200 ${isFilterDropdownOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {isFilterDropdownOpen && (
                <div className="absolute left-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                  <ul className="py-2">
                    <li className="mx-2 my-0">
                      <button
                        type="button"
                        onClick={() => handleStatusFilterSelect(null)}
                        className="w-full rounded-md px-4 py-2 text-left text-sm font-semibold text-[#1C1B1F] hover:bg-primary hover:text-white"
                      >
                        All Services
                      </button>
                    </li>
                    {orderProcessingStatusFilters.serviceOrderStatus.map(
                      (status) => (
                        <li key={status.value} className="mx-2 my-0">
                          <button
                            type="button"
                            onClick={() => handleStatusFilterSelect(status.value)}
                            className="w-full rounded-md px-4 py-2 text-left text-sm font-semibold text-[#1C1B1F] hover:bg-primary hover:text-white"
                          >
                            {status.label}
                          </button>
                        </li>
                      ),
                    )}
                  </ul>
                </div>
              )}
            </div>
            <div className="relative">
              <input
                type="text"
                placeholder="Search..."
                onChange={(e) => setSearchKey(e.target.value)}
                value={searchKey}
                className="border p-2 pl-3 pr-10 w-80 accent-primary focus:outline-primary border-gray-300 rounded"
              />
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
              />
            </div>
          </div>
        </div>
        <div className="shadow-lg mt-3 rounded-lg">
          <form>
            <table className="w-full table-auto rounded-xl">
              <thead className="bg-gray-100">
                <tr className="border-b">
                  {/* Order No. */}
                  <th className="px-4 py-2 text-center font-semibold">
                    <button
                      type="button"
                      onClick={() => handleSort('orderNumber')}
                      className="flex items-center justify-center gap-1 w-full focus:outline-none"
                    >
                      Order No.
                      {renderSortIcon('orderNumber')}
                    </button>
                  </th>
                  {/* Customer */}
                  <th className="px-4 py-2 text-center font-semibold">
                    <button
                      type="button"
                      onClick={() => handleSort('customerName')}
                      className="flex items-center justify-center gap-1 w-full focus:outline-none"
                    >
                      Customer
                      {renderSortIcon('customerName')}
                    </button>
                  </th>
                  {/* Created On */}
                  <th className="px-4 py-2 text-center font-semibold">
                    <button
                      type="button"
                      onClick={() => handleSort('createdDate')}
                      className="flex items-center justify-center gap-1 w-full focus:outline-none"
                    >
                      Created On
                      {renderSortIcon('createdDate')}
                    </button>
                  </th>
                  {/* Service Status */}
                  <th className="px-2 py-2 text-center font-semibold">
                    <button
                      type="button"
                      onClick={() => handleSort('status')}
                      className="flex items-center justify-center gap-1 w-full focus:outline-none"
                    >
                      Service Status
                      {renderSortIcon('status')}
                    </button>
                  </th>
                  <th className="px-4 py-2 text-center font-semibold">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {serviceProcessingList.length > 0 ? (
                  serviceProcessingList.map((serviceProcessingList) => (
                    <tr
                      key={serviceProcessingList.orderNumber}
                      onClick={() => {
                        handleDetailsView(
                          serviceProcessingList.id,
                          navigate,
                          FrontendRoutes.SERVICE_PROCESS_TASK_LIST,
                        );
                        setSelectedOrderId(serviceProcessingList.id);
                      }}
                      className={`hover:cursor-pointer hover:bg-tertiary hover:border hover:border-primary hover:b-2 ${selectedOrderId === serviceProcessingList.id
                        ? 'bg-tertiary border border-primary'
                        : ''
                        }`}
                    >
                      <td className="px-4 py-2 text-center whitespace-nowrap">
                        {serviceProcessingList.orderNumber}
                      </td>
                      <td className=" px-4 py-2 text-center whitespace-nowrap">
                        {serviceProcessingList.customerName}
                      </td>
                      <td className="px-4 py-2 text-center whitespace-nowrap">
                        {formatDate(String(serviceProcessingList.createdAt || ''))}
                      </td>
                      <td className="flex justify-center items-start px-4 py-2 whitespace-nowrap">
                        <OrderStatusBadge
                          type="orderServiceStatus"
                          status={serviceProcessingList.serviceStatus ?? 0}
                        />
                      </td>
                      <td className="px-4 py-2 text-center relative">
                        <button
                          type="button"
                          className="p-4"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleDropdown(serviceProcessingList.id);
                          }}
                        >
                          <FontAwesomeIcon icon={faEllipsisVertical} />
                        </button>
                        {activeDropdown === serviceProcessingList.id && (
                          <div
                            className="absolute right-0 mt-2 w-56 bg-white border rounded-lg shadow-lg z-10"
                            ref={(el) => {
                              if (el) {
                                dropdownRefs.current.set(
                                  serviceProcessingList.id,
                                  el,
                                );
                              } else {
                                dropdownRefs.current.delete(
                                  serviceProcessingList.id,
                                );
                              }
                            }}
                          >
                            <ul className="py-2 text-left">
                              <li className="mx-2 my-0">
                                <button
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleDetailsView(
                                      serviceProcessingList.id,
                                      navigate,
                                      FrontendRoutes.ORDER_DETAILS,
                                    );
                                  }}
                                  type="button"
                                  className="w-full px-4 py-2 text-sm font-semibold hover:bg-[#EFF1F5] cursor-pointer rounded-md text-left"
                                >
                                  View order summary
                                </button>
                              </li>
                              <li className="mx-2 my-0">
                                <button
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleDetailsView(
                                      serviceProcessingList.id,
                                      navigate,
                                      FrontendRoutes.SERVICE_PROCESS_TASK_LIST,
                                    );
                                  }}
                                  type="button"
                                  className="w-full px-4 py-2 text-sm font-semibold hover:bg-[#EFF1F5] cursor-pointer rounded-md text-left"
                                >
                                  View service processing
                                </button>
                              </li>
                              <li className="mx-2 my-0">
                                <button
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleDetailsView(
                                      serviceProcessingList.id,
                                      navigate,
                                      FrontendRoutes.PRODUCT_PROCESSING_TASK_LIST,
                                    );
                                  }}
                                  type="button"
                                  className="w-full px-4 py-2 text-sm font-semibold hover:bg-[#EFF1F5] cursor-pointer rounded-md text-left"
                                >
                                  View product processing
                                </button>
                              </li>

                              <li className="mx-2 my-0">
                                <button
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleDetailsView(
                                      serviceProcessingList.id,
                                      navigate,
                                      FrontendRoutes.INVOICE_ORDERED_ITEMS,
                                    );
                                  }}
                                  type="button"
                                  className="w-full px-4 py-2 text-sm font-semibold hover:bg-[#EFF1F5] cursor-pointer rounded-md text-left"
                                >
                                  View invoices
                                </button>
                              </li>
                            </ul>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="px-4 py-2 text-center">
                      No data available.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </form>

          {/* Pagination */}
          <div className="bottom-0">
            <Pagination
              totalPages={totalPages}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          </div>
          <div className="modal-divs">
            {isCreatingNewOrder && (
              <CreateOrderModal
                onClose={() => {
                  setisCreatingNewOrder(false);
                }}
                fromList="service_processing"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};